import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Pressable, Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';

const TIMEOUT_MS = 20_000;
const DEFAULT_AUTH_REDIRECT = '/kursi-learn';


const AUTH_DEBUG_PREFIX = '[AuthFlow]';
const logAuthDebug = (event: string, details?: Record<string, unknown>) => {
  if (__DEV__) {
    console.log(`${AUTH_DEBUG_PREFIX} ${event}`, details ?? {});
  }
};

const getPkceLocalStorageState = (): { keyCount: number; keys: string[] } => {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return { keyCount: 0, keys: [] };
  }

  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (key.toLowerCase().includes('pkce') || key.toLowerCase().includes('code-verifier')) {
      keys.push(key);
    }
  }

  return { keyCount: keys.length, keys };
};

const normalizeInternalRoute = (candidate: unknown): string | null => {
  if (typeof candidate !== 'string') return null;

  const value = candidate.trim();
  if (!value) return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//')) return null;
  if (value.includes('://')) return null;

  return value;
};

const readLocalStorageReturnTarget = (): string | null => {
  if (Platform.OS !== 'web' || typeof localStorage === 'undefined') return null;

  try {
    const saved = localStorage.getItem('kp_return_to');
    localStorage.removeItem('kp_return_to');
    return normalizeInternalRoute(saved);
  } catch {
    return null;
  }
};

export default function AuthCallbackScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ code?: string; next?: string; returnTo?: string; error?: string; error_description?: string }>();

  const [status, setStatus] = useState('מאמת פרטים...');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const finishedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runIdRef = useRef(0);

  const requestedTarget = useMemo(
    () => normalizeInternalRoute(params.returnTo) ?? normalizeInternalRoute(params.next),
    [params.next, params.returnTo],
  );

  const finish = useCallback(() => {
    finishedRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const safeNavigate = useCallback(
    (target?: string | null) => {
      finish();
      router.replace((target ?? DEFAULT_AUTH_REDIRECT) as any);
    },
    [finish, router],
  );

  useEffect(() => {
    let mounted = true;
    runIdRef.current += 1;
    const runId = runIdRef.current;

    finishedRef.current = false;
    setErrorMsg(null);
    setStatus('מאמת פרטים...');

    timeoutRef.current = setTimeout(() => {
      if (!mounted || runIdRef.current !== runId || finishedRef.current) return;
      finish();
      setStatus('');
      setErrorMsg('האימות לוקח יותר מדי זמן. נסה שוב.');
    }, TIMEOUT_MS);

    const completeAuth = async () => {
      try {
        logAuthDebug('callback_loaded', {
          href: Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.href : null,
          origin: Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.origin : null,
          hasCode: !!params.code,
          pkce: getPkceLocalStorageState(),
        });
        if (params.error) {
          throw new Error(params.error_description || params.error);
        }

        const code = typeof params.code === 'string' ? params.code : undefined;

        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();

        if (code && !currentSession) {
          logAuthDebug('before_exchangeCodeForSession', {
            hasCode: true,
            pkce: getPkceLocalStorageState(),
          });

          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            logAuthDebug('exchangeCodeForSession_failure', {
              message: exchangeError.message,
              name: (exchangeError as Error)?.name,
            });
            throw exchangeError;
          }

          logAuthDebug('exchangeCodeForSession_success', { pkce: getPkceLocalStorageState() });
        }

        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        logAuthDebug('post_session_check', {
          hasSession: !!session,
          userId: session?.user?.id ?? null,
        });

        if (!session?.user?.id) {
          safeNavigate(DEFAULT_AUTH_REDIRECT);
          return;
        }

        setStatus('מסיים התחברות...');

        const localStorageTarget = readLocalStorageReturnTarget();
        const redirectTarget = requestedTarget ?? localStorageTarget ?? DEFAULT_AUTH_REDIRECT;

        safeNavigate(redirectTarget);
      } catch (error) {
        if (!mounted || runIdRef.current !== runId || finishedRef.current) return;
        console.error('Auth callback error:', error);
        logAuthDebug('callback_failure', {
          message: (error as Error)?.message,
          name: (error as Error)?.name,
          pkce: getPkceLocalStorageState(),
        });
        finish();
        setStatus('');
        setErrorMsg('לא הצלחנו לאמת את ההתחברות. נסה שוב.');
      }
    };

    void completeAuth();

    return () => {
      mounted = false;
      runIdRef.current += 1;
      finish();
    };
  }, [finish, params.code, params.error, params.error_description, requestedTarget, safeNavigate]);

  const retry = () => {
    const target = requestedTarget ?? readLocalStorageReturnTarget() ?? DEFAULT_AUTH_REDIRECT;
    safeNavigate(target);
  };

  if (errorMsg) {
    return (
      <View style={styles.container}>
        {Platform.OS === 'web' && (
          <Head>
            <meta name="robots" content="noindex, nofollow" />
            <link rel="canonical" href="https://kursiaii.com/auth/callback" />
          </Head>
        )}

        <View style={styles.card}>
          <Text style={styles.title}>משהו השתבש</Text>
          <Text style={styles.text}>{errorMsg}</Text>

          <Pressable style={styles.primaryBtn} onPress={retry}>
            <Text style={styles.primaryBtnText}>המשך</Text>
          </Pressable>

          <Pressable style={styles.linkBtn} onPress={() => safeNavigate(DEFAULT_AUTH_REDIRECT)}>
            <Text style={styles.linkBtnText}>חזרה ל-Kursi Learn</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' && (
        <Head>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://kursiaii.com/auth/callback" />
        </Head>
      )}

      <ActivityIndicator size="large" color={theme.colors.primary} />
      <Text style={styles.statusText}>{status}</Text>
      <Text style={styles.subText}>אם זה נמשך יותר מדי זמן, אפשר לרענן את הדף.</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    padding: theme.spacing.xl,
  },
  card: {
    width: '100%',
    maxWidth: 460,
    padding: theme.spacing.xl,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.text,
    gap: theme.spacing.md,
  },
  title: {
    fontFamily: theme.fonts.serifBold,
    fontSize: 32,
    color: theme.colors.text,
    textAlign: 'center',
    letterSpacing: -0.6,
  },
  text: {
    fontFamily: theme.fonts.regular,
    fontSize: 16,
    color: theme.colors.textLight,
    textAlign: 'center',
    lineHeight: 24,
  },
  primaryBtn: {
    backgroundColor: theme.colors.text,
    borderWidth: 1,
    borderColor: theme.colors.text,
    paddingVertical: theme.spacing.md,
    alignItems: 'center',
    marginTop: theme.spacing.sm,
  },
  primaryBtnText: {
    fontFamily: theme.fonts.bold,
    color: theme.colors.background,
    fontSize: 14,
    letterSpacing: 0.3,
  },
  linkBtn: {
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  linkBtnText: {
    fontFamily: theme.fonts.mono,
    color: theme.colors.text,
    fontSize: 11,
    letterSpacing: 1.6,
    textTransform: 'uppercase' as any,
  },
  statusText: {
    fontFamily: theme.fonts.serifMedium,
    fontSize: 24,
    color: theme.colors.text,
    marginTop: theme.spacing.lg,
    textAlign: 'center',
    letterSpacing: -0.3,
  },
  subText: {
    fontFamily: theme.fonts.mono,
    fontSize: 11,
    color: theme.colors.textMuted,
    textAlign: 'center',
    marginTop: theme.spacing.md,
    letterSpacing: 1.4,
    textTransform: 'uppercase' as any,
  },
});
